import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './SolicitationDetail.css';

const SECTION_TYPE_ORDER = [
  'cover', 'sow', 'min_qual', 'eval_criteria', 'special_terms',
  'quote_sheet', 'qa', 'addendum', 'boilerplate', 'other',
];

const SECTION_TYPE_LABEL = {
  cover:          'Cover / Header',
  sow:            'Scope of Work',
  min_qual:       'Minimum Qualifications',
  eval_criteria:  'Evaluation Criteria',
  special_terms:  'Special Terms & Conditions',
  quote_sheet:    'Quote Sheet / Pricing',
  qa:             'Pre-Bid Q & A',
  addendum:       'Addendum',
  boilerplate:    'Boilerplate (DGS Standard Terms)',
  other:          'Other',
};

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }); }
  catch (_) { return s; }
}
function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { dateStyle:'medium', timeStyle:'short' }); }
  catch (_) { return s; }
}
function fmtMoney(n) {
  if (n == null || isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { style:'currency', currency:'USD', maximumFractionDigits: 2 }).format(Number(n));
}

export default function SolicitationDetail() {
  const { id } = useParams();
  useDocumentTitle(id ? `Solicitation ${id}` : 'Solicitation');

  const { data, isLoading, error } = useQuery({
    queryKey: ['solicitation', id],
    queryFn: () => apiClient.get(`/api/solicitations/${encodeURIComponent(id)}`),
    enabled: !!id,
  });

  const [expandedSections, setExpandedSections] = useState({});
  const [showBoilerplate, setShowBoilerplate] = useState(false);
  const [downloadError, setDownloadError] = useState(null); // { attachment_id, message }

  // PO-link review (confirm / reject candidate matches).
  // PATCH /api/solicitations/:id/po-links/:link_id { status }
  const queryClient = useQueryClient();
  const reviewLink = useMutation({
    mutationFn: ({ link_id, status }) =>
      apiClient.request(`/api/solicitations/${encodeURIComponent(id)}/po-links/${link_id}`, {
        method: 'PATCH',
        body: { status },
      }),
    onMutate: ({ link_id, status }) => {
      // Optimistic — flip the status pill immediately; roll back on failure.
      const previous = queryClient.getQueryData(['solicitation', id]);
      if (previous?.linked_pos) {
        queryClient.setQueryData(['solicitation', id], {
          ...previous,
          linked_pos: previous.linked_pos.map((l) =>
            l.link_id === link_id ? { ...l, status } : l
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['solicitation', id], ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['solicitation', id] }),
  });

  if (isLoading) {
    return <div className="sol-detail-shell"><div className="sol-loading">Loading solicitation {id}…</div></div>;
  }
  if (error) {
    return <div className="sol-detail-shell"><div className="sol-error">Error: {error.message}</div></div>;
  }
  if (!data || !data.header) {
    return <div className="sol-detail-shell"><div className="sol-error">Solicitation {id} not found.</div></div>;
  }

  const { header, attachments, sections, skus, linked_pos, linked_contracts = [], parsed_audit, summary } = data;

  // Sections grouped by attachment_id
  const sectionsByAttachment = {};
  for (const s of sections) {
    (sectionsByAttachment[s.attachment_id] ||= []).push(s);
  }
  // SKUs grouped by attachment_id
  const skusByAttachment = {};
  for (const k of skus) {
    (skusByAttachment[k.attachment_id] ||= []).push(k);
  }

  const handleDownload = async (attachment_id) => {
    setDownloadError(null);
    try {
      const { url } = await apiClient.get(`/api/solicitations/${encodeURIComponent(id)}/attachments/${attachment_id}/download`);
      window.open(url, '_blank');
    } catch (err) {
      setDownloadError({ attachment_id, message: err.message });
    }
  };

  return (
    <div className="sol-detail-shell">
      {/* Breadcrumb */}
      <div className="sol-breadcrumb">
        <Link to="/solicitations">Solicitations</Link>
        <span className="sol-breadcrumb-sep">›</span>
        <span className="sol-breadcrumb-current">{id}</span>
      </div>

      {/* Header card */}
      <header className="sol-header-card">
        <div className="sol-header-top">
          <div className="sol-header-id">{header.solicitation_id}</div>
          <div className={`sol-status sol-status-${(header.status_raw || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}>
            {header.status_raw || 'unknown'}
          </div>
        </div>
        <h1 className="sol-header-title">{header.title || '(untitled)'}</h1>
        <div className="sol-header-meta">
          <div className="sol-meta-row">
            <span className="sol-meta-label">Department:</span>
            <span>{header.department_acronym ? `${header.department_acronym} — ` : ''}{header.department_full_name || header.department_name_raw || '—'}</span>
          </div>
          <div className="sol-meta-row">
            <span className="sol-meta-label">Published:</span><span>{fmtDate(header.published_date)}</span>
            <span className="sol-meta-label">Closes:</span><span>{fmtDateTime(header.close_date)}</span>
          </div>
          {parsed_audit && parsed_audit[0] && (
            <div className="sol-meta-row">
              <span className="sol-meta-label">RFQ #:</span><span>{parsed_audit[0].rfq_number || '—'}</span>
              <span className="sol-meta-label">Contact:</span><span>{parsed_audit[0].contact_name || '—'} {parsed_audit[0].contact_email ? `<${parsed_audit[0].contact_email}>` : ''}</span>
            </div>
          )}
        </div>
        <div className="sol-header-counts">
          <span className="sol-count-chip">{summary.attachment_count} attachments</span>
          <span className="sol-count-chip">{summary.section_count} sections</span>
          <span className={`sol-count-chip ${summary.sku_count > 0 ? 'sol-count-chip-highlight' : ''}`}>{summary.sku_count} SKUs</span>
          <span className={`sol-count-chip ${summary.linked_po_count > 0 ? 'sol-count-chip-highlight' : ''}`}>{summary.linked_po_count} linked POs</span>
        </div>
      </header>

      {/* SKU table — surfaced at top because it's the highest-value content */}
      {skus.length > 0 && (
        <section className="sol-section-card">
          <div className="sol-section-header">
            <h2>SKUs Bid in this Solicitation</h2>
            <span className="sol-section-subtitle">{skus.length} line items extracted from the quote sheet</span>
          </div>
          <div className="sol-sku-table-wrap">
            <table className="sol-sku-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Vendor</th>
                  <th>Description</th>
                  <th className="sol-num">Qty</th>
                  <th className="sol-num">Unit $</th>
                  <th className="sol-num">Line $</th>
                  <th>Warranty</th>
                </tr>
              </thead>
              <tbody>
                {skus.map(s => (
                  <tr key={s.sku_row_id} className={s.is_optional ? 'sol-sku-optional' : ''}>
                    <td className="sol-sku-code">{s.sku || '—'}</td>
                    <td>{s.vendor || '—'}</td>
                    <td className="sol-sku-desc">{s.description || '—'}</td>
                    <td className="sol-num">{s.quantity != null ? s.quantity : '—'}</td>
                    <td className="sol-num">{fmtMoney(s.unit_price)}</td>
                    <td className="sol-num">{fmtMoney(s.line_total)}</td>
                    <td className="sol-sku-warranty">{s.warranty || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Linked master contracts (H.18.3.A — Pass-1 LPA-contract-ID join).
          When this RFQ cites an LPA master agreement that's already in
          our PO history, we surface the contract + its PO history here. */}
      <section className="sol-section-card">
        <div className="sol-section-header">
          <h2>Linked Master Contracts</h2>
          <span className="sol-section-subtitle">
            {linked_contracts.length === 0
              ? 'No master-contract citations detected — this RFQ may be a standalone procurement, or the historical detail sweep has not run against it yet'
              : `${linked_contracts.length} LPA contract reference${linked_contracts.length === 1 ? '' : 's'} found in PDF body`}
          </span>
        </div>
        {linked_contracts.length > 0 && (
          <table className="sol-link-table">
            <thead>
              <tr>
                <th>LPA Contract</th>
                <th>Method</th>
                <th className="sol-num">Confidence</th>
                <th className="sol-num">POs Issued</th>
                <th className="sol-num">Total Spend</th>
                <th>Active Range</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {linked_contracts.map(c => (
                <tr key={c.link_id}>
                  <td className="sol-sku-code">{c.lpa_contract_id}</td>
                  <td>{c.match_method}</td>
                  <td className="sol-num">{Number(c.confidence).toFixed(2)}</td>
                  <td className="sol-num">{Number(c.po_count).toLocaleString()}</td>
                  <td className="sol-num">{fmtMoney(c.total_spend)}</td>
                  <td>{c.earliest_po_date ? `${fmtDate(c.earliest_po_date)} → ${fmtDate(c.latest_po_date)}` : '—'}</td>
                  <td>{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Linked POs — candidate matches from the deterministic cross-walk.
          All start in 'candidate' status; operator confirms or rejects. */}
      <section className="sol-section-card">
        <div className="sol-section-header">
          <h2>Linked Purchase Orders</h2>
          <span className="sol-section-subtitle">
            {linked_pos.length === 0
              ? 'No PO candidates — run the deterministic matcher to populate'
              : `${linked_pos.length} candidate PO${linked_pos.length === 1 ? '' : 's'} (review and confirm)`}
          </span>
        </div>
        {linked_pos.length > 0 && (
          <table className="sol-link-table">
            <thead>
              <tr>
                <th>Conf</th>
                <th>Supplier</th>
                <th>Amount</th>
                <th>PO Date</th>
                <th>Description</th>
                <th>Status</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {linked_pos.map(l => {
                const pending = reviewLink.isPending && reviewLink.variables?.link_id === l.link_id;
                const isCandidate = l.status === 'candidate';
                return (
                  <tr key={l.link_id} className={`sol-link-row sol-link-row-${l.status}`}>
                    <td>{l.confidence != null ? Number(l.confidence).toFixed(2) : '—'}</td>
                    <td>{l.supplier_name || '—'}</td>
                    <td>{l.line_total != null ? `$${Number(l.line_total).toLocaleString()}` : '—'}</td>
                    <td>{l.purchase_order_date ? l.purchase_order_date.slice(0, 10) : '—'}</td>
                    <td className="sol-link-desc" title={l.item_description || ''}>
                      {(l.item_description || '').slice(0, 80)}{(l.item_description || '').length > 80 ? '…' : ''}
                    </td>
                    <td>
                      <span className={`sol-link-status-pill sol-link-status-${l.status}`}>{l.status}</span>
                    </td>
                    <td className="sol-link-actions">
                      {isCandidate ? (
                        <>
                          <button
                            type="button"
                            className="sol-link-btn sol-link-btn-confirm"
                            disabled={pending}
                            onClick={() => reviewLink.mutate({ link_id: l.link_id, status: 'confirmed' })}
                            title="Confirm this PO matches the RFQ"
                          >Confirm</button>
                          <button
                            type="button"
                            className="sol-link-btn sol-link-btn-reject"
                            disabled={pending}
                            onClick={() => reviewLink.mutate({ link_id: l.link_id, status: 'rejected' })}
                            title="Reject — not the right PO"
                          >Reject</button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="sol-link-btn sol-link-btn-reopen"
                          disabled={pending}
                          onClick={() => reviewLink.mutate({ link_id: l.link_id, status: 'candidate' })}
                          title="Re-open for review"
                        >Reopen</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {reviewLink.isError && (
          <div className="sol-error" style={{ marginTop: 8 }}>
            Review failed: {reviewLink.error?.message || 'unknown error'}
          </div>
        )}
      </section>

      {/* Attachments + sections accordion */}
      {attachments.map(att => {
        const attSections = (sectionsByAttachment[att.attachment_id] || []).sort((a, b) => {
          const aOrder = SECTION_TYPE_ORDER.indexOf(a.section_type);
          const bOrder = SECTION_TYPE_ORDER.indexOf(b.section_type);
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.position - b.position;
        });
        const attSkus = skusByAttachment[att.attachment_id] || [];
        return (
          <section className="sol-section-card" key={att.attachment_id}>
            <div className="sol-section-header">
              <h2>{att.filename}</h2>
              <div className="sol-attachment-actions">
                <span className="sol-meta-label">{attSections.length} sections, {attSkus.length} SKUs</span>
                <button type="button" className="sol-action-btn" onClick={() => handleDownload(att.attachment_id)}>
                  Download original PDF
                </button>
              </div>
            </div>
            {downloadError?.attachment_id === att.attachment_id && (
              <div className="sol-error" style={{ marginBottom: 8 }}>
                Download failed: {downloadError.message}
              </div>
            )}
            {att.parse_state === 'failed' && (
              <div className="sol-parse-error">Parse failed: {att.parse_error || 'unknown error'}</div>
            )}
            <div className="sol-section-accordion">
              {attSections.map(sec => {
                const isExpanded = expandedSections[sec.section_id] || false;
                const shouldHide = sec.is_boilerplate && !showBoilerplate;
                if (shouldHide) return null;
                return (
                  <div key={sec.section_id} className={`sol-accordion-item sol-section-type-${sec.section_type}`}>
                    <button
                      type="button"
                      className="sol-accordion-toggle"
                      onClick={() => setExpandedSections(s => ({ ...s, [sec.section_id]: !isExpanded }))}>
                      <span className="sol-accordion-chevron">{isExpanded ? '▾' : '▸'}</span>
                      <span className="sol-accordion-title">
                        {SECTION_TYPE_LABEL[sec.section_type] || sec.section_type}
                        {sec.section_label && sec.section_label !== sec.section_type && (
                          <span className="sol-accordion-label"> — {sec.section_label}</span>
                        )}
                      </span>
                      <span className="sol-accordion-meta">
                        pp {sec.page_start}-{sec.page_end} · {(sec.raw_text || '').length.toLocaleString()} chars
                        {sec.is_boilerplate && <span className="sol-boilerplate-badge"> boilerplate</span>}
                      </span>
                    </button>
                    {isExpanded && (
                      <pre className="sol-accordion-body">{sec.raw_text || '(no text extracted)'}</pre>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Toggle for boilerplate visibility — only show if there's boilerplate to reveal */}
            {attSections.some(s => s.is_boilerplate) && (
              <div className="sol-boilerplate-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={showBoilerplate}
                    onChange={(e) => setShowBoilerplate(e.target.checked)}
                  />
                  Show boilerplate sections (DGS Standard Terms etc.)
                </label>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
